<script>
import FilterPanel from '@/components/common/FilterPanel.vue';
import FilterField from '@/components/common/FilterField.vue';
import MultiSelectFilter from '@/components/common/MultiSelectFilter.vue';
import DateRangeFilter from '@/components/common/DateRangeFilter.vue';
import SavedFilters from '@/components/common/SavedFilters.vue';
import { SAVED_FILTER_SCOPES } from '@/shared/services/saved-filter.service';

/**
 * Les critères de la liste des entreprises.
 *
 * Gouvernorat et secteur se saisissaient en texte libre: il fallait connaître
 * l'orthographe exacte de ce qui est en base pour obtenir une ligne. Les deux
 * sont maintenant des choix multiples peuplés depuis la base elle-même, et
 * l'année de création comme la date d'ajout se filtrent par intervalle.
 */
export default {
  name: 'CompaniesFilters',
  components: {
    FilterPanel,
    FilterField,
    MultiSelectFilter,
    DateRangeFilter,
    SavedFilters,
  },
  props: {
    modelValue: { type: Object, required: true },
    /** { governorates, sectors, yearFoundedMin, yearFoundedMax } */
    options: {
      type: Object,
      default: () => ({
        governorates: [],
        sectors: [],
        yearFoundedMin: null,
        yearFoundedMax: null,
      }),
    },
    activeCount: { type: Number, default: 0 },
    criteria: { type: Object, default: () => ({}) },
    appliedFilterId: { type: String, default: null },
    expanded: { type: Boolean, default: false },
  },
  emits: [
    'update:modelValue',
    'update:expanded',
    'reset',
    'apply-saved',
    'saved-filters-loaded',
  ],
  data() {
    return {
      scope: SAVED_FILTER_SCOPES.COMPANIES,
      surveyOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Au moins une enquête' },
        { value: 'no', label: 'Aucune enquête' },
      ],
      emailOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Renseignée' },
        { value: 'no', label: 'Non renseignée' },
      ],
      regimeOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Offshore' },
        { value: 'no', label: 'Non offshore' },
      ],
    };
  },
  computed: {
    governorateChoices() {
      return (this.options.governorates || []).map((value) => ({
        value,
        label: value,
      }));
    },

    sectorChoices() {
      return (this.options.sectors || []).map((value) => ({
        value,
        label: value,
      }));
    },

    yearPlaceholders() {
      return {
        from: this.options.yearFoundedMin
          ? String(this.options.yearFoundedMin)
          : 'Depuis',
        to: this.options.yearFoundedMax
          ? String(this.options.yearFoundedMax)
          : "Jusqu'à",
      };
    },
  },
  methods: {
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },

    /** Une année vide vaut « pas de borne », pas « année zéro ». */
    updateYear(bound, value) {
      const year = value === '' ? null : Number(value);

      this.update('yearFounded', {
        ...this.modelValue.yearFounded,
        [bound]: Number.isNaN(year) ? null : year,
      });
    },
  },
};
</script>

<template>
  <FilterPanel
    :model-value="modelValue.searchQuery"
    :expanded="expanded"
    :active-count="activeCount"
    search-placeholder="Rechercher une raison sociale, une activité…"
    @update:model-value="update('searchQuery', $event)"
    @update:expanded="$emit('update:expanded', $event)"
    @reset="$emit('reset')"
  >
    <template #saved>
      <SavedFilters
        :scope="scope"
        :criteria="criteria"
        :applied-id="appliedFilterId"
        @apply="$emit('apply-saved', $event)"
        @loaded="$emit('saved-filters-loaded', $event)"
      />
    </template>

    <template #advanced>
      <FilterField label="Gouvernorats">
        <MultiSelectFilter
          :model-value="modelValue.governorates"
          :options="governorateChoices"
          placeholder="Tous les gouvernorats"
          @update:model-value="update('governorates', $event)"
        />
      </FilterField>

      <FilterField label="Secteurs d'activité">
        <MultiSelectFilter
          :model-value="modelValue.sectors"
          :options="sectorChoices"
          placeholder="Tous les secteurs"
          @update:model-value="update('sectors', $event)"
        />
      </FilterField>

      <FilterField label="Enquêtes rattachées">
        <select
          class="list-select"
          :value="modelValue.hasSurveys"
          data-testid="companies-has-surveys"
          @change="update('hasSurveys', $event.target.value)"
        >
          <option
            v-for="option in surveyOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Adresse électronique">
        <select
          class="list-select"
          :value="modelValue.hasEmail"
          data-testid="companies-has-email"
          @change="update('hasEmail', $event.target.value)"
        >
          <option
            v-for="option in emailOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Régime">
        <select
          class="list-select"
          :value="modelValue.isOffshore"
          data-testid="companies-offshore"
          @change="update('isOffshore', $event.target.value)"
        >
          <option
            v-for="option in regimeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Année de création">
        <div class="date-range__inputs">
          <input
            type="number"
            class="list-input"
            :placeholder="yearPlaceholders.from"
            :value="modelValue.yearFounded.from ?? ''"
            data-testid="companies-year-from"
            @input="updateYear('from', $event.target.value)"
          />
          <span class="date-range__sep">→</span>
          <input
            type="number"
            class="list-input"
            :placeholder="yearPlaceholders.to"
            :value="modelValue.yearFounded.to ?? ''"
            data-testid="companies-year-to"
            @input="updateYear('to', $event.target.value)"
          />
        </div>
      </FilterField>

      <FilterField label="Date d'ajout" wide>
        <DateRangeFilter
          :model-value="modelValue.dateAdded"
          @update:model-value="update('dateAdded', $event)"
        />
      </FilterField>
    </template>
  </FilterPanel>
</template>
